import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, X } from 'lucide-react';
import { formatPercentPoints, type StrainSummary } from '@shared';
import { useRevokeObjectUrls } from '../hooks/useRevokeObjectUrls';
import { getProductRepository } from '../lib/productRepository';
import { revokeDisplayObjectUrls } from '../lib/productImages/objectUrls';
import {
  doseCalculatorReducer,
  doseProductDisplayName,
  doseProductMatchesSearch,
  isAvailableCbdPercent,
  isUsableThcPercent,
  summarizeDose,
} from '../lib/doseCalculator';
import { EmptyState, ErrorState, LoadingState } from '../components/ui';
import { ritualDraftFromProducts } from '../lib/ritualModel';

function summaryBlobUrls(rows: StrainSummary[]): Array<string | null> {
  return rows.flatMap((product) => [
    product.localPrimaryThumbnailUrl,
    product.firstLocalThumbnailUrl,
    product.primaryThumbnailUrl,
  ]);
}

function formatDoseNumber(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
  }).format(value);
}

function ProductThumb({ product }: { product: StrainSummary }): JSX.Element {
  if (product.primaryThumbnailUrl) {
    return (
      <img
        className="dose-product__thumb"
        src={product.primaryThumbnailUrl}
        alt=""
      />
    );
  }
  return (
    <span className="dose-product__thumb dose-product__thumb--placeholder" aria-hidden>
      🌿
    </span>
  );
}

function ProductPotency({ product }: { product: StrainSummary }): JSX.Element {
  return (
    <div className="dose-product__potency muted">
      <span>{formatPercentPoints(product.thcPercent)} THC</span>
      {isAvailableCbdPercent(product.cbdPercent) ? (
        <span>{formatPercentPoints(product.cbdPercent)} CBD</span>
      ) : null}
    </div>
  );
}

function DoseProductPicker({
  products,
  selectedIds,
  onSelect,
  onClose,
}: {
  products: StrainSummary[];
  selectedIds: ReadonlySet<string>;
  onSelect: (product: StrainSummary) => void;
  onClose: () => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(
    () => products.filter((product) => doseProductMatchesSearch(product, search)),
    [products, search],
  );

  useEffect(() => {
    searchRef.current?.focus();
    const closeFromEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeFromEscape);
    return () => document.removeEventListener('keydown', closeFromEscape);
  }, [onClose]);

  return (
    <div
      className="dose-picker"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="dose-picker__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dose-picker-title"
      >
        <header className="dose-picker__header">
          <div>
            <h2 id="dose-picker-title">Select Strain</h2>
            <p className="muted">Choose a product from your active Library.</p>
          </div>
          <button
            type="button"
            className="icon-btn"
            aria-label="Close strain selector"
            onClick={onClose}
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        <div className="search-bar">
          <Search className="search-bar__icon" size={16} aria-hidden />
          <label htmlFor="dose-product-search" className="visually-hidden">
            Search Library products
          </label>
          <input
            ref={searchRef}
            id="dose-product-search"
            className="search-bar__input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, brand, Product ID…"
          />
        </div>

        <div className="dose-picker__list" role="listbox">
          {filtered.map((product) => {
            const alreadySelected = selectedIds.has(product.id);
            const unavailable = !isUsableThcPercent(product.thcPercent);
            const disabled = alreadySelected || unavailable;
            return (
              <button
                key={product.id}
                type="button"
                role="option"
                aria-selected={alreadySelected}
                disabled={disabled}
                className="dose-picker__option"
                onClick={() => onSelect(product)}
              >
                <ProductThumb product={product} />
                <span className="dose-picker__option-body">
                  <strong>{doseProductDisplayName(product)}</strong>
                  {product.strainClassSourceText ? (
                    <span className="muted">{product.strainClassSourceText}</span>
                  ) : null}
                  {unavailable ? (
                    <span className="error-text">THC unavailable</span>
                  ) : alreadySelected ? (
                    <span className="muted">Already added</span>
                  ) : (
                    <ProductPotency product={product} />
                  )}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 ? (
            <p className="muted dose-picker__no-results">No matching products.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function DoseCalculator(): JSX.Element {
  const navigate = useNavigate();
  const [products, setProducts] = useState<StrainSummary[]>([]);
  const [selections, dispatch] = useReducer(doseCalculatorReducer, []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    getProductRepository()
      .list({ archived: 'active', sort: 'name' })
      .then((rows) => {
        if (cancelled) {
          revokeDisplayObjectUrls(summaryBlobUrls(rows));
          return;
        }
        setProducts(rows);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(
            error instanceof Error ? error.message : 'Could not load Library products.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useRevokeObjectUrls(summaryBlobUrls(products));

  const summary = useMemo(() => summarizeDose(selections), [selections]);
  const selectedIds = useMemo(
    () => new Set(selections.map(({ product }) => product.id)),
    [selections],
  );
  const closePicker = (): void => {
    setPickerOpen(false);
    requestAnimationFrame(() => addButtonRef.current?.focus());
  };

  return (
    <div className="stack dose-page">
      <header className="dose-page__header">
        <div>
          <h1 className="page-title">Dose Calculator</h1>
          <p className="page-lead">
            Select your strains and enter how much of each you plan to use.
          </p>
        </div>
        <Link to="/calculator" className="btn btn-secondary dose-page__blend-link">
          Blend Calculator
        </Link>
      </header>

      <p className="hint dose-page__disclaimer">
        Results are theoretical cannabinoid content in the flower material, not
        absorbed or systemic dose.
      </p>

      {loading ? <LoadingState label="Loading Library…" /> : null}
      {loadError ? <ErrorState message={loadError} /> : null}

      {!loading && !loadError && selections.length === 0 ? (
        <EmptyState
          title="No strains selected yet"
          description="Select a strain to start calculating your bowl or blend."
          action={
            <button
              ref={addButtonRef}
              type="button"
              className="btn btn-primary"
              onClick={() => setPickerOpen(true)}
            >
              Select Strain
            </button>
          }
        />
      ) : null}

      {selections.length > 0 ? (
        <>
          <div className="dose-products">
            {selections.map(({ product, weightInput }, index) => {
              const contribution = summary.contributions[index];
              const invalidWeight =
                weightInput.trim() !== '' &&
                (!Number.isFinite(Number(weightInput)) || Number(weightInput) < 0);
              return (
                <article key={product.id} className="card dose-product">
                  <div className="dose-product__identity">
                    <ProductThumb product={product} />
                    <div className="dose-product__identity-body">
                      <h2>{doseProductDisplayName(product)}</h2>
                      {product.strainClassSourceText ? (
                        <div className="muted">{product.strainClassSourceText}</div>
                      ) : null}
                      <ProductPotency product={product} />
                    </div>
                    <button
                      type="button"
                      className="icon-btn dose-product__remove"
                      aria-label={`Remove ${doseProductDisplayName(product)}`}
                      onClick={() =>
                        dispatch({ type: 'remove', productId: product.id })
                      }
                    >
                      <X size={18} aria-hidden />
                    </button>
                  </div>

                  <div className="dose-product__calculation">
                    <div className="field dose-product__weight">
                      <label htmlFor={`dose-weight-${product.id}`}>Weight</label>
                      <div className="dose-product__weight-control">
                        <input
                          id={`dose-weight-${product.id}`}
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={weightInput}
                          onChange={(event) =>
                            dispatch({
                              type: 'set_weight',
                              productId: product.id,
                              value: event.target.value,
                            })
                          }
                          aria-invalid={invalidWeight}
                        />
                        <span aria-hidden>g</span>
                      </div>
                      {invalidWeight ? (
                        <span className="error-text">Enter zero or a positive weight.</span>
                      ) : null}
                    </div>

                    <dl className="dose-product__contributions">
                      <div>
                        <dt>THC contribution</dt>
                        <dd>{formatDoseNumber(contribution?.thcMg ?? 0)} mg</dd>
                      </div>
                      {contribution?.cbdMg != null ? (
                        <div>
                          <dt>CBD contribution</dt>
                          <dd>{formatDoseNumber(contribution.cbdMg)} mg</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                </article>
              );
            })}
          </div>

          <button
            ref={addButtonRef}
            type="button"
            className="btn dose-page__add"
            onClick={() => setPickerOpen(true)}
          >
            <Plus size={17} aria-hidden />
            Add Strain
          </button>

          <section className="card dose-summary" aria-labelledby="dose-total-title">
            <div className="dose-summary__eyebrow">Total</div>
            <h2 id="dose-total-title" className="visually-hidden">
              Dose summary
            </h2>
            <div className="dose-summary__primary">
              <strong>{formatDoseNumber(summary.totalThcMg)}</strong>
              <span>mg THC</span>
            </div>
            {summary.totalCbdMg != null ? (
              <div className="dose-summary__cbd">
                {formatDoseNumber(summary.totalCbdMg)} mg CBD
              </div>
            ) : null}
            <dl className="dose-summary__details">
              <div>
                <dt>Total Weight</dt>
                <dd>{formatDoseNumber(summary.totalWeightGrams, 4)} g</dd>
              </div>
              <div>
                <dt>Selected Products</dt>
                <dd>{summary.selectedCount}</dd>
              </div>
              {summary.effectiveThcPercent != null ? (
                <div>
                  <dt>Effective Blend THC</dt>
                  <dd>{formatDoseNumber(summary.effectiveThcPercent)}%</dd>
                </div>
              ) : null}
            </dl>
          </section>

          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={summary.totalWeightGrams <= 0}
            onClick={() => navigate('/rituals', { state: { ritualDraft: ritualDraftFromProducts(selections.map(({ product, weightInput }, index) => ({ product, weightGrams: Number(weightInput) || 0, thcDoseMg: summary.contributions[index]?.thcMg ?? 0 })), 'weight', 'dose-calculator') } })}
          >
            Save Ritual
          </button>

          <button
            type="button"
            className="btn dose-page__clear"
            onClick={() => dispatch({ type: 'clear' })}
          >
            <Trash2 size={16} aria-hidden />
            Clear Calculator
          </button>
        </>
      ) : null}

      {pickerOpen ? (
        <DoseProductPicker
          products={products}
          selectedIds={selectedIds}
          onSelect={(product) => {
            dispatch({ type: 'add', product });
            closePicker();
          }}
          onClose={closePicker}
        />
      ) : null}
    </div>
  );
}
